/** @format */
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { TranslateService } from '@ngx-translate/core';
import { CourseUnitResultItem, OtmId, SearchParameters, SearchResult, SearchValidity } from 'common-typescript/types';
import { isEqual, omit } from 'lodash-es';
import { combineLatest, distinctUntilChanged, finalize, Observable, of, ReplaySubject, shareReplay, Subject, switchMap } from 'rxjs';
import { catchError, map, takeUntil, tap } from 'rxjs/operators';
import { AuthService, singleConcurrentSearchWithThrottle } from 'sis-common';
import {
    AppErrorHandler,
    ComboboxOption,
    CourseUnitEntityService,
    CurriculumPeriodEntityService,
    Option,
    SearchService,
    silentErrorHandler,
    UniversityService,
} from 'sis-components';
import { CourseCartEntityService } from '../../common/service/course-cart-entity.service';
import { OpenUniversitySearchFilters } from '../search.types';
import { getStudyPeriodInfoLoader } from '../search.utils';

const RESULTS_PER_PAGE = 20;

@Component({
    selector: 'app-search-open-university',
    templateUrl: './search-open-university.component.html',
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: SearchService,
            useFactory: () =>
                new SearchService<OpenUniversitySearchFilters>({
                    storageKey: 'searchParameters.student.search.open-university',
                    universityOrgIdEnabled: false,
                    defaultOptions: {
                        start: 0,
                        limit: RESULTS_PER_PAGE,
                    },
                }),
        },
    ],
    standalone: false,
})
export class SearchOpenUniversityComponent implements OnInit, OnDestroy {
    currentPage = 1;

    courseCartCourseUnitIds: OtmId[] = [];

    destroyed$ = new Subject<void>();

    /**
     * Indicates whether a search request is pending.
     */
    searching = false;

    readonly salesPeriodOptions: ComboboxOption[] = [
        { label: this.translateService.translate('SEARCH_PAGE_OPEN_UNIVERSITY_ONGOING'), value: 'ONGOING' },
        { label: this.translateService.translate('SEARCH_PAGE_OPEN_UNIVERSITY_FUTURE'), value: 'FUTURE' },
    ];

    searchSortOptions: Option[] = [
        { label: this.translate.instant('SEARCH.SORT_SELECTOR.SORT_METHOD_OPTIONS.MATCH'), value: null },
        { label: this.translate.instant('SEARCH.SORT_SELECTOR.SORT_METHOD_OPTIONS.NAME_ASC'), value: 'name' },
        { label: this.translate.instant('SEARCH.SORT_SELECTOR.SORT_METHOD_OPTIONS.NAME_DESC'), value: '-name' },
        { label: this.translate.instant('SEARCH.SORT_SELECTOR.SORT_METHOD_OPTIONS.CREDITS_ASC'), value: 'credits' },
        { label: this.translate.instant('SEARCH.SORT_SELECTOR.SORT_METHOD_OPTIONS.CREDITS_DESC'), value: '-credits' },
    ];

    private readonly validParameters$ = new ReplaySubject<SearchParameters<OpenUniversitySearchFilters>>();
    readonly searchResult$ = this.validParameters$.pipe(
        singleConcurrentSearchWithThrottle(parameters => this.executeSearch(parameters)),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    readonly curriculumPeriodIds$: Observable<OtmId[]> = this.searchService.searchParameters$.pipe(
        map(params => params.filters?.curriculumPeriodId ?? []),
        map(ids => (Array.isArray(ids) ? ids : [ids]).filter(Boolean)),
        distinctUntilChanged(isEqual),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    readonly curriculumPeriods$ = this.searchService.universityOrgId$.pipe(
        switchMap(id => this.curriculumPeriodService.getByUniversityOrgId(id).pipe(catchError(() => of([])))),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    readonly studyPeriodInfos$ = getStudyPeriodInfoLoader(this.searchService.universityOrgId$);

    readonly viewData$ = combineLatest({
        curriculumPeriods: this.curriculumPeriods$,
        studyPeriodInfos: this.studyPeriodInfos$,
    });

    constructor(
        private authService: AuthService,
        private courseCartEntityService: CourseCartEntityService,
        private courseUnitService: CourseUnitEntityService,
        private curriculumPeriodService: CurriculumPeriodEntityService,
        private translate: TranslateService,
        private translateService: TranslocoService,
        private appErrorHandler: AppErrorHandler,
        private universityService: UniversityService,
        protected searchService: SearchService<OpenUniversitySearchFilters>,
    ) {}

    ngOnInit(): void {
        if (this.isLoggedIn()) {
            this.courseCartEntityService
                .getCourseCart(true)
                .pipe(takeUntil(this.destroyed$), this.appErrorHandler.defaultErrorHandler())
                .subscribe(ids => (this.courseCartCourseUnitIds = ids));
        }
    }

    ngOnDestroy() {
        this.destroyed$.next();
    }

    onSearchParameterChange(parameters: SearchParameters<OpenUniversitySearchFilters>) {
        this.validParameters$.next(parameters);
    }

    isLoggedIn(): boolean {
        return this.authService.loggedIn();
    }

    sort(sortValue: string) {
        this.searchService.sort(sortValue);
    }

    addCourseUnitToCourseCart(courseUnitId: string) {
        this.courseCartEntityService
            .addCurToCourseCart(courseUnitId)
            .pipe(takeUntil(this.destroyed$), this.appErrorHandler.defaultErrorHandler())
            .subscribe();
    }

    removeCourseUnitFromCourseCart(courseUnitId: string) {
        this.courseCartEntityService
            .deleteCurFromCourseCart(courseUnitId)
            .pipe(takeUntil(this.destroyed$), this.appErrorHandler.defaultErrorHandler())
            .subscribe();
    }

    onPaginationChange(newPage: number) {
        this.currentPage = newPage;
        this.searchService.patchOptions({ start: (newPage - 1) * RESULTS_PER_PAGE });
        document.getElementById('results-show-guide')?.focus();
    }

    get resultsPerPage() {
        return RESULTS_PER_PAGE;
    }

    private executeSearch({
        filters,
        options,
    }: SearchParameters<OpenUniversitySearchFilters>): Observable<SearchResult<CourseUnitResultItem>> {
        this.searching = true;
        let salesPeriodValidity: SearchValidity;
        if (filters.salesPeriodValidity?.includes('ONGOING') && filters.salesPeriodValidity?.includes('FUTURE')) {
            salesPeriodValidity = 'ONGOING_AND_FUTURE';
        } else {
            salesPeriodValidity = filters.salesPeriodValidity?.[0];
        }
        return this.courseUnitService
            .searchActive({
                ...options,
                ...omit(filters, ['attainmentLanguage', 'studyLevel', 'teachingLanguage', 'enrolmentPeriodValidity']),
                assessmentItemType: filters.assessmentItemType,
                codeUrn: [...(filters.attainmentLanguage ?? []), ...(filters.studyLevel ?? [])],
                curCodeUrn: filters.teachingLanguage,
                salesPeriodValidity,
                sort: options.sort?.join(','),
                studyType: 'OPEN_UNIVERSITY_STUDIES',
                universityOrgId: this.universityService.getCurrentUniversityOrgId(),
                validity: 'ONGOING_AND_FUTURE',
            })
            .pipe(
                tap(results => (this.currentPage = Math.floor((results?.start ?? 0) / RESULTS_PER_PAGE) + 1)),
                finalize(() => (this.searching = false)),
                silentErrorHandler,
            ) as Observable<SearchResult<CourseUnitResultItem>>;
    }
}
